import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Icon from '../UI/Icon';
import { auth, db } from '../../firebase.ts';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
  getProblematicSounds,
  getProblematicSoundsBySeverity,
  getMispronunciationOptions,
  getWordDisplayInfo,
  getAvailableWords,
  PRONUNCIATION_TESTS
} from '../../services/pronunciationMapping';
import {
  createNewTest,
  updateTestProgress,
  completeTest,
  deleteTest
} from '../../services/pronunciationTestService';
import './AusspracheTestModal.css';

/**
 * Helper function to get the display text for a mispronunciation option
 * @param {string} targetWord - The word being tested
 * @param {string} mispronunciationOption - The mispronunciation option key
 * @returns {string} The display text to show to the user
 */
const getMispronunciationDisplayText = (targetWord, mispronunciationOption) => {
  const testData = PRONUNCIATION_TESTS[targetWord];
  if (!testData || !testData.mispronunciations[mispronunciationOption]) {
    return mispronunciationOption; // Fallback to option key if not found
  }
  return testData.mispronunciations[mispronunciationOption].displayText;
};

function AusspracheTestModal({ show, onClose, initialState }) {
  // Track the current test document ID (from new pronunciationTests collection)
  const [currentTestId, setCurrentTestId] = useState(initialState?.id || null);

  // Initialize state from saved state if available
  const [currentStep, setCurrentStep] = useState(initialState?.data?.currentStep || 1);
  const [selectedYears, setSelectedYears] = useState(initialState?.data?.selectedYears || 3);
  const [selectedMonths, setSelectedMonths] = useState(initialState?.data?.selectedMonths || 0);
  const [currentWordIndex, setCurrentWordIndex] = useState(initialState?.data?.currentWordIndex || 0);
  const [selectedMispronunciations, setSelectedMispronunciations] = useState([]);
  const [tongueBetweenTeeth, setTongueBetweenTeeth] = useState({}); // Object to track tongue for each mispronunciation
  const [customPronunciation, setCustomPronunciation] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completedTests, setCompletedTests] = useState(initialState?.data?.wordTests || []);
  const [extractedSoundIssues, setExtractedSoundIssues] = useState([]);
  const [severeProblemSounds, setSevereProblemSounds] = useState([]);
  const [mildProblemSounds, setMildProblemSounds] = useState([]);
  const [wordImages, setWordImages] = useState({}); // Store images for each word
  const [pauseConfirmation, setPauseConfirmation] = useState(false); // Show pause confirmation

  // Update local state when initialState prop changes (from database)
  // Also reset to fresh state when initialState is null
  useEffect(() => {
    if (initialState?.id && initialState?.data) {
      // Restore from saved state
      setCurrentTestId(initialState.id);
      setCurrentStep(initialState.data.currentStep || 1);
      setSelectedYears(initialState.data.selectedYears || 3);
      setSelectedMonths(initialState.data.selectedMonths || 0);
      const restoredWordIndex = initialState.data.currentWordIndex || 0;
      setCurrentWordIndex(restoredWordIndex);

      // Handle wordTests - ensure dates are properly converted
      const restoredTests = (initialState.data.wordTests || []).map(test => ({
        ...test,
        completedAt: test.completedAt instanceof Date ? test.completedAt : new Date(test.completedAt),
        selectedMispronunciations: test.selectedMispronunciations || [] // For backward compatibility
      }));
      setCompletedTests(restoredTests);

      // Restore UI state for current word if there's a completed test for it
      const currentWords = getAvailableWords();
      const currentWord = currentWords[restoredWordIndex];
      const currentWordTest = restoredTests.find(test => test.word === currentWord);

      if (currentWordTest) {
        setSelectedMispronunciations(currentWordTest.selectedMispronunciations || []);
        setTongueBetweenTeeth(currentWordTest.tongueBetweenTeeth || {});
        setCustomPronunciation(currentWordTest.customPronunciation || '');
        setShowCustomInput(!!currentWordTest.customPronunciation);
      } else {
        // Reset UI state if no completed test for current word
        setSelectedMispronunciations([]);
        setTongueBetweenTeeth({});
        setCustomPronunciation('');
        setShowCustomInput(false);
      }
    } else {
      // No initialState - reset everything to fresh state
      setCurrentTestId(null);
      setCurrentStep(1);
      setSelectedYears(3);
      setSelectedMonths(0);
      setCurrentWordIndex(0);
      setCompletedTests([]);
      setSelectedMispronunciations([]);
      setTongueBetweenTeeth({});
      setCustomPronunciation('');
      setShowCustomInput(false);
    }
  }, [initialState]);

  // Load word images from Words collection
  const loadWordImages = useCallback(async () => {
    try {
      const availableWords = getAvailableWords();
      
      const imagePromises = availableWords.map(async (word) => {
        try {
          const q = query(
            collection(db, 'Words'),
            where('word', '==', word)
          );
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const wordDoc = querySnapshot.docs[0];
            const wordData = wordDoc.data();
            
            const linkedImage = wordData.linkedImage || wordData.LinkedImage;
            
            if (linkedImage) {
              return { word, imageUrl: linkedImage };
            }
          }
          return { word, imageUrl: null };
        } catch (error) {
          console.error(`❌ Error fetching image for word ${word}:`, error);
          return { word, imageUrl: null };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      
      const imageMap = {};
      imageResults.forEach(({ word, imageUrl }) => {
        if (imageUrl) {
          imageMap[word] = imageUrl;
        }
      });
      
      setWordImages(imageMap);
    } catch (error) {
      console.error('❌ Error loading word images:', error);
    }
  }, []);

  // Load problem sounds when reaching step 4
  useEffect(() => {
    if (currentStep === 4) {
      loadProblemSounds();
    }
  }, [currentStep]);

  // Load word images when modal opens
  useEffect(() => {
    if (show) {
      loadWordImages();
    }
  }, [show, loadWordImages]);

  // Also load images when reaching step 3 (pronunciation test)
  useEffect(() => {
    if (currentStep === 3 && Object.keys(wordImages).length === 0) {
      loadWordImages();
    }
  }, [currentStep, wordImages, loadWordImages]);

  // Helper function to convert years and months to decimal age
  const calculateDecimalAge = (years, months) => {
    const decimalAge = years + (months / 12);
    return Math.round(decimalAge * 10) / 10; // Round to 1 decimal place
  };

  // Save the current test state to the database (NEW: uses pronunciationTests collection)
  const saveTestState = async (updates = {}) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const currentYears = updates.selectedYears ?? selectedYears;
      const currentMonths = updates.selectedMonths ?? selectedMonths;

      const stateToSave = {
        currentStep: updates.currentStep ?? currentStep,
        currentWordIndex: updates.currentWordIndex ?? currentWordIndex,
        wordTests: updates.completedTests ?? completedTests,
        selectedYears: currentYears,
        selectedMonths: currentMonths
      };

      if (currentTestId) {
        // Update existing test document
        await updateTestProgress(currentTestId, stateToSave);
      } else {
        // Create new test document (should happen on first save)
        const testId = await createNewTest(user.uid, {
          years: currentYears,
          months: currentMonths,
          decimal: calculateDecimalAge(currentYears, currentMonths)
        });
        setCurrentTestId(testId);

        // Update the newly created document with current state
        await updateTestProgress(testId, stateToSave);
      }
    } catch (error) {
      console.error('Error saving test state:', error);
    }
  };

  // Clear the test state when test is fully completed or cancelled
  const clearTestState = async () => {
    try {
      if (currentTestId) {
        // Delete the test document
        await deleteTest(currentTestId);
        setCurrentTestId(null);
      }
    } catch (error) {
      console.error('Error clearing test state:', error);
    }
  };

  const extractProblemSoundsFromTests = (allTests) => {
    const allSoundIssues = allTests
      .filter(test => test.soundIssues && Array.isArray(test.soundIssues))
      .flatMap(test => test.soundIssues)
      .filter(sound => sound);
    
    return [...new Set(allSoundIssues)];
  };

  const extractProblemSoundsBySeverity = (allTests) => {
    const mildSounds = [];
    const severeSounds = [];
    
    allTests.forEach(test => {
      // Check if test has severity-based structure
      if (test.soundsBySeverity) {
        if (Array.isArray(test.soundsBySeverity.mild)) {
          mildSounds.push(...test.soundsBySeverity.mild);
        }
        if (Array.isArray(test.soundsBySeverity.severe)) {
          severeSounds.push(...test.soundsBySeverity.severe);
        }
      } else if (test.soundIssues && Array.isArray(test.soundIssues)) {
        // Fallback for older tests without severity data - assume severe
        severeSounds.push(...test.soundIssues);
      }
    });

    // Remove duplicates
    const uniqueMildSounds = [...new Set(mildSounds)];
    const uniqueSevereSounds = [...new Set(severeSounds)];
    const allSounds = [...new Set([...mildSounds, ...severeSounds])];

    return {
      mild: uniqueMildSounds,
      severe: uniqueSevereSounds,
      all: allSounds
    };
  };

  // Load problem sounds from current test document
  const loadProblemSounds = async () => {
    try {
      if (currentTestId) {
        // Load from current test document (uses completedTests state)
        const problemSounds = extractProblemSoundsFromTests(completedTests);
        const problemSoundsBySeverity = extractProblemSoundsBySeverity(completedTests);

        setExtractedSoundIssues(problemSounds);
        setSevereProblemSounds(problemSoundsBySeverity.severe);
        setMildProblemSounds(problemSoundsBySeverity.mild);
      }
    } catch (error) {
      console.error('Error loading problem sounds:', error);
    }
  };

  const handleAgeSubmit = async () => {
    setSaving(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const decimalAge = calculateDecimalAge(selectedYears, selectedMonths);

        await updateDoc(doc(db, 'users', user.uid), {
          childData: {
            age: decimalAge, // Keep decimal age for backward compatibility
            ageYears: selectedYears,
            ageMonths: selectedMonths
          }
        });
        // Move to next step and save state
        setCurrentStep(2);
        await saveTestState({
          currentStep: 2,
          selectedYears: selectedYears,
          selectedMonths: selectedMonths
        });
      }
    } catch (error) {
      console.error('Error saving child age:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInfoContinue = async () => {
    setSaving(true);
    try {
      // Move to pronunciation test step
      setCurrentStep(3);
      await saveTestState({
        currentStep: 3
      });
    } catch (error) {
      console.error('Error saving info page state:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePronunciationSubmit = async () => {
    setSaving(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const availableWords = getAvailableWords();
        const targetWord = availableWords[currentWordIndex];

        // Calculate problematic sounds based on selected mispronunciations and age
        const decimalAge = calculateDecimalAge(selectedYears, selectedMonths);
        const soundIssues = getProblematicSounds(targetWord, selectedMispronunciations, decimalAge);
        const soundsBySeverity = getProblematicSoundsBySeverity(targetWord, selectedMispronunciations, decimalAge);

        const pronunciationTest = {
          word: targetWord,
          soundIssues: soundIssues, // Keep for backward compatibility
          soundsBySeverity: soundsBySeverity, // New severity-based structure
          tongueBetweenTeeth: tongueBetweenTeeth,
          customPronunciation: customPronunciation.trim() || null,
          selectedMispronunciations: selectedMispronunciations,
          completedAt: new Date()
        };

        // Check if we're updating an existing test or adding a new one
        const existingTestIndex = completedTests.findIndex(test => test.word === targetWord);
        let newCompletedTests;

        if (existingTestIndex !== -1) {
          // Update existing test
          newCompletedTests = [...completedTests];
          newCompletedTests[existingTestIndex] = pronunciationTest;
        } else {
          // Add new test
          newCompletedTests = [...completedTests, pronunciationTest];
        }
        setCompletedTests(newCompletedTests);

        // Check if there are more words to test
        if (currentWordIndex < availableWords.length - 1) {
          // Move to next word
          const nextWordIndex = currentWordIndex + 1;
          const nextTargetWord = availableWords[nextWordIndex];

          // Check if there's already a completed test for the next word
          const nextCompletedTest = newCompletedTests.find(test => test.word === nextTargetWord);

          if (nextCompletedTest) {
            // Restore state from the existing completed test
            restoreStateFromCompletedTest(nextCompletedTest);
          } else {
            // No completed test, start fresh
            setSelectedMispronunciations([]);
            setTongueBetweenTeeth({});
            setCustomPronunciation('');
            setShowCustomInput(false);
          }

          setCurrentWordIndex(nextWordIndex);

          // Save state with new word index
          await saveTestState({
            currentWordIndex: nextWordIndex,
            completedTests: newCompletedTests
          });
        } else {
          // All words completed - complete the test in the new collection
          const problemSounds = extractProblemSoundsFromTests(newCompletedTests);
          const problemSoundsBySeverity = extractProblemSoundsBySeverity(newCompletedTests);

          // Complete the test document with final results
          await completeTest(currentTestId, {
            wordTests: newCompletedTests,
            problemSounds: problemSounds,
            mildProblemSounds: problemSoundsBySeverity.mild,
            severeProblemSounds: problemSoundsBySeverity.severe,
            currentStep: 4
          });

          // Move to completion step
          setCurrentStep(4);
        }
      }
    } catch (error) {
      console.error('Error saving pronunciation test:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestComplete = async () => {
    // Test is already marked as completed in handlePronunciationSubmit
    // Just reset the modal state and close
    setCurrentStep(1);
    setSelectedYears(3);
    setSelectedMonths(0);
    setCurrentWordIndex(0);
    setSelectedMispronunciations([]);
    setTongueBetweenTeeth({});
    setCustomPronunciation('');
    setShowCustomInput(false);
    setCompletedTests([]);
    setCurrentTestId(null);

    // Close the modal
    onClose();
  };

  const handleClose = async () => {
    // Note: We no longer clear the test state when closing early
    // The progress is preserved in the database for resuming later

    // Reset the modal state
    setCurrentStep(1);
    setSelectedYears(3);
    setSelectedMonths(0);
    setCurrentWordIndex(0);
    setSelectedMispronunciations([]);
    setTongueBetweenTeeth({});
    setCustomPronunciation('');
    setShowCustomInput(false);
    setCompletedTests([]);
    setCurrentTestId(null);

    // Allow closing by calling the onClose prop
    onClose();
  };

  const toggleMispronunciation = (option) => {
    setSelectedMispronunciations(prev => {
      const isCurrentlySelected = prev.includes(option);
      
      if (isCurrentlySelected) {
        // If deselecting the option, also deselect its tongue button
        setTongueBetweenTeeth(prevTongue => ({
          ...prevTongue,
          [option]: false
        }));
        return prev.filter(item => item !== option);
      } else {
        // If selecting an option, handle mutual exclusivity
        let newSelections;
        
        if (option === 'correct') {
          // If selecting "correct", deselect all other options and hide custom input
          setTongueBetweenTeeth({ [option]: false }); // Reset all tongue states except this one
          setShowCustomInput(false);
          setCustomPronunciation('');
          newSelections = [option];
        } else {
          // If selecting a mispronunciation, deselect "correct" and hide custom input
          const filteredPrev = prev.filter(item => item !== 'correct');
          setTongueBetweenTeeth(prevTongue => ({
            ...prevTongue,
            correct: false,
            custom: false
          }));
          setShowCustomInput(false);
          setCustomPronunciation('');
          newSelections = [...filteredPrev, option];
        }
        
        return newSelections;
      }
    });
  };

  const toggleTongueForOption = (option) => {
    // Check if the tongue button should be enabled
    if (!isTongueButtonEnabled(option)) {
      return; // Don't allow toggle if corresponding option isn't selected
    }
    
    setTongueBetweenTeeth(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Helper function to determine if a tongue button should be enabled
  const isTongueButtonEnabled = (option) => {
    if (option === 'correct') {
      return selectedMispronunciations.includes('correct');
    } else if (option === 'custom') {
      return showCustomInput || (customPronunciation.trim() !== '');
    } else {
      // Regular mispronunciation options
      return selectedMispronunciations.includes(option);
    }
  };

  // Function to restore UI state from a completed test
  const restoreStateFromCompletedTest = (completedTest) => {
    setSelectedMispronunciations(completedTest.selectedMispronunciations || []);
    setTongueBetweenTeeth(completedTest.tongueBetweenTeeth || {});
    setCustomPronunciation(completedTest.customPronunciation || '');
    setShowCustomInput(!!completedTest.customPronunciation); // Show input if there was custom pronunciation
  };

  // Handle pause test - save progress and close
  const handlePauseTest = async () => {
    setSaving(true);
    try {
      await saveTestState({
        currentStep,
        currentWordIndex,
        completedTests
      });
      setPauseConfirmation(true);

      // Auto-close after showing confirmation (4 seconds to allow reading the message)
      setTimeout(() => {
        setPauseConfirmation(false);
        // Reset modal state
        setCurrentStep(1);
        setSelectedYears(3);
        setSelectedMonths(0);
        setCurrentWordIndex(0);
        setSelectedMispronunciations([]);
        setTongueBetweenTeeth({});
        setCustomPronunciation('');
        setShowCustomInput(false);
        setCompletedTests([]);
        setCurrentTestId(null);
        onClose();
      }, 4000);
    } catch (error) {
      console.error('Error pausing test:', error);
    } finally {
      setSaving(false);
    }
  };

  // Remove age restrictions - all children should get the same test
  const availableWords = getAvailableWords();
  const targetWord = availableWords[currentWordIndex] || availableWords[0];
  const wordDisplayInfo = getWordDisplayInfo(targetWord);
  const mispronunciationOptions = getMispronunciationOptions(targetWord);

  // Helper function to format age display
  const formatAgeDisplay = (years, months) => {
    if (months === 0) {
      return `${years} Jahre`;
    } else {
      return `${years} Jahre, ${months} Monate`;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="md"
      centered
      backdrop={true}
      keyboard={true}
      className="aussprache-test-modal"
    >
      <Modal.Header className="aussprache-test-header">
        <div className="text-center w-100">
          {currentStep === 3 ? (
            <p className="text-muted mb-2" style={{ fontSize: '1rem', fontWeight: 'normal' }}>
              Wort {currentWordIndex + 1} von {availableWords.length}
            </p>
          ) : (
            <h2 className="aussprache-title mb-2">
              Aussprachetest
            </h2>
          )}
          <p className="aussprache-subtitle mb-0">
            {currentStep === 1 ? 'Wie alt ist dein Kind?' :
             currentStep === 2 ? 'Wichtige Information vor dem Test' :
             currentStep === 3 ? '' :
             'Lass uns herausfinden, welche Laute wir üben sollten'}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="aussprache-test-body">
        {currentStep === 1 && (
          <div className="age-selection-content">
            <div className="text-center mb-4">
              <Icon name="profile" size="lg" className="mb-2 text-primary" />
              <h4 className="mb-2">Alter des Kindes</h4>
              <p className="text-muted mb-3">
                Diese Information hilft uns, den Test an das Entwicklungslevel anzupassen.
              </p>
              <div className="age-display mb-3">
                <h5 className="text-dark">{formatAgeDisplay(selectedYears, selectedMonths)}</h5>
              </div>
            </div>
            
            <Row className="justify-content-center">
              <Col md={10}>
                <div className="age-sliders">
                  {/* Years Slider */}
                  <div className="mb-4">
                    <Form.Label className="slider-label d-flex justify-content-between">
                      <span>Jahre</span>
                      <span className="text-dark font-weight-bold">{selectedYears}</span>
                    </Form.Label>
                    <Form.Range
                      min="1"
                      max="12"
                      step="1"
                      value={selectedYears}
                      onChange={(e) => setSelectedYears(parseInt(e.target.value))}
                      className="age-range-slider large-thumb"
                      style={{
                        '--bs-form-range-thumb-width': '32px',
                        '--bs-form-range-thumb-height': '32px',
                        '--bs-form-range-thumb-border-radius': '50%',
                        '--bs-form-range-thumb-bg': '#000000',
                        '--bs-form-range-thumb-border': '2px solid #000000'
                      }}
                    />
                  </div>
                  
                  {/* Months Slider */}
                  <div className="mb-3">
                    <Form.Label className="slider-label d-flex justify-content-between">
                      <span>Monate</span>
                      <span className="text-dark font-weight-bold">{selectedMonths}</span>
                    </Form.Label>
                    <Form.Range
                      min="0"
                      max="11"
                      step="1"
                      value={selectedMonths}
                      onChange={(e) => setSelectedMonths(parseInt(e.target.value))}
                      className="age-range-slider large-thumb"
                      style={{
                        '--bs-form-range-thumb-width': '32px',
                        '--bs-form-range-thumb-height': '32px',
                        '--bs-form-range-thumb-border-radius': '50%',
                        '--bs-form-range-thumb-bg': '#000000',
                        '--bs-form-range-thumb-border': '2px solid #000000'
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {currentStep === 2 && (
          <div className="info-page-content">
            <div className="text-center mb-4">
              <div className="tongue-icon-large mb-4" style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100px',
                height: '100px',
                border: '3px solid #8B5CF6',
                borderRadius: 'var(--radius-md)',
                backgroundColor: '#F3E8FF'
              }}>
                <Icon name="tongue" size="xl" style={{ color: '#8B5CF6', fontSize: '3rem' }} />
              </div>
              <h4 className="mb-3" style={{ fontWeight: 'bold', color: '#1F2937' }}>
                Information zum Symbol
              </h4>
              <p className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto' }}>
                Klicken Sie auf dieses Symbol, wenn Ihr Kind beim Sprechen die Zunge nach vorne zwischen die Zähne schiebt,
                sodass sie sichtbar hervorsteht, insbesondere bei s-, z- und sch-Lauten.
              </p>
            </div>

            <div className="tongue-example-section" style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              maxWidth: '450px',
              margin: '0 auto'
            }}>
              <h6 className="text-center mb-3" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#6B7280' }}>
                So sieht das Symbol aus:
              </h6>
              <div className="d-flex justify-content-center align-items-center gap-3">
                <div style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: '#FFFFFF',
                  color: '#6B7280',
                  fontWeight: '500',
                  fontSize: '0.95rem'
                }}>
                  Aussprache-Option
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '42px',
                  height: '42px',
                  border: '2px solid #8B5CF6',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: '#FFFFFF',
                  color: '#8B5CF6'
                }}>
                  <Icon name="tongue" size="md" />
                </div>
              </div>
              <p className="text-center text-muted mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                Dieses Symbol erscheint neben jeder Aussprache-Option
              </p>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="pronunciation-test-content">
            {/* Pause confirmation overlay */}
            {pauseConfirmation && (
              <div className="pause-confirmation-overlay">
                <div className="pause-confirmation-content">
                  <div className="pause-check-icon">✓</div>
                  <h5>Fortschritt gespeichert!</h5>
                  <p className="text-muted mb-0">
                    Du kannst den Test jederzeit fortsetzen.<br />
                    <strong>Gehe zu deinem Profil und scrolle nach unten</strong>, um den Test fortzusetzen.
                  </p>
                </div>
              </div>
            )}

            <div className="text-center mb-3">
              <div>
                <h2 className="word-display">{wordDisplayInfo.displayWord}</h2>
                {wordImages[targetWord] && (
                  <div className="word-image-container">
                    <img
                      src={wordImages[targetWord]}
                      alt={wordDisplayInfo.displayWord}
                      className="word-image"
                      style={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}
                      onError={(e) => {
                        console.error(`❌ Image failed to load for "${targetWord}":`, wordImages[targetWord]);
                        // Hide image if it fails to load
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="mispronunciation-selection">
              <h5 className="text-center mb-3">Wie hat dein Kind das Wort ausgesprochen?</h5>
              <p className="text-center text-muted mb-3">
                Bitte wähle alle zutreffenden Optionen aus:
              </p>
              
              <Row className="justify-content-center">
                <Col md={8}>
                  <div className="mispronunciation-options">
                    {mispronunciationOptions.map((option) => (
                      <div key={option} className="mispronunciation-option-container">
                        <div className="mispronunciation-option-wrapper">
                          <Form.Check
                            type="checkbox"
                            id={`mispronunciation-${option}`}
                            value={option}
                            checked={selectedMispronunciations.includes(option)}
                            onChange={() => toggleMispronunciation(option)}
                            className="mispronunciation-checkbox d-none"
                          />
                          <label 
                            htmlFor={`mispronunciation-${option}`} 
                            className={`mispronunciation-option-label ${
                              selectedMispronunciations.includes(option) ? 'selected' : ''
                            }`}
                          >
                            {getMispronunciationDisplayText(targetWord, option)}
                          </label>
                        </div>
                        
                        {/* Tongue button for this specific option */}
                        <div className="tongue-button-wrapper">
                          <Form.Check
                            type="checkbox"
                            id={`tongue-${option}`}
                            checked={tongueBetweenTeeth[option] || false}
                            onChange={() => toggleTongueForOption(option)}
                            className="mispronunciation-checkbox d-none"
                          />
                          <label 
                            htmlFor={`tongue-${option}`} 
                            className={`tongue-button ${
                              tongueBetweenTeeth[option] ? 'selected' : ''
                            } ${!isTongueButtonEnabled(option) ? 'disabled' : ''}`}
                            title={isTongueButtonEnabled(option) ? "Zunge zwischen den Zähnen" : "Wähle zuerst eine Aussprache-Option"}
                          >
                            <Icon name="tongue" size="md" />
                          </label>
                        </div>
                      </div>
                    ))}
                    
                    {/* Custom pronunciation button/input */}
                    {!showCustomInput ? (
                      <div className="mispronunciation-option-container">
                        <div className="mispronunciation-option-wrapper">
                          <button 
                            type="button"
                            onClick={() => {
                              setShowCustomInput(true);
                              // Clear all other selections when showing custom input
                              setSelectedMispronunciations([]);
                              setTongueBetweenTeeth({ custom: false }); // Reset all tongue states
                            }}
                            className="mispronunciation-option-label custom-option"
                          >
                            <Icon name="edit" size="sm" className="me-2" />
                            Andere Aussprache
                          </button>
                        </div>
                        
                        {/* Tongue button for custom pronunciation */}
                        <div className="tongue-button-wrapper">
                          <Form.Check
                            type="checkbox"
                            id="tongue-custom"
                            checked={tongueBetweenTeeth['custom'] || false}
                            onChange={() => toggleTongueForOption('custom')}
                            className="mispronunciation-checkbox d-none"
                          />
                          <label 
                            htmlFor="tongue-custom" 
                            className={`tongue-button ${
                              tongueBetweenTeeth['custom'] ? 'selected' : ''
                            } ${!isTongueButtonEnabled('custom') ? 'disabled' : ''}`}
                            title={isTongueButtonEnabled('custom') ? "Zunge zwischen den Zähnen" : "Gib zuerst eine andere Aussprache ein"}
                          >
                            <Icon name="tongue" size="md" />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="custom-pronunciation-wrapper">
                        <div className="custom-pronunciation-label">
                          <Icon name="edit" size="sm" className="me-2" />
                          Andere Aussprache:
                        </div>
                        
                        <div className="custom-pronunciation-input-container">
                          <Form.Control
                            type="text"
                            placeholder="Wie hat das Kind das Wort ausgesprochen?"
                            value={customPronunciation}
                            onChange={(e) => setCustomPronunciation(e.target.value)}
                            className="custom-pronunciation-input"
                            autoFocus
                          />
                          
                          {/* Tongue button next to input */}
                          <div className="tongue-button-wrapper">
                            <Form.Check
                              type="checkbox"
                              id="tongue-custom-input"
                              checked={tongueBetweenTeeth['custom'] || false}
                              onChange={() => toggleTongueForOption('custom')}
                              className="mispronunciation-checkbox d-none"
                            />
                            <label 
                              htmlFor="tongue-custom-input" 
                              className={`tongue-button ${
                                tongueBetweenTeeth['custom'] ? 'selected' : ''
                              } ${!isTongueButtonEnabled('custom') ? 'disabled' : ''}`}
                              title={isTongueButtonEnabled('custom') ? "Zunge zwischen den Zähnen" : "Gib zuerst eine andere Aussprache ein"}
                            >
                              <Icon name="tongue" size="md" />
                            </label>
                          </div>
                        </div>
                        
                        <button 
                          type="button"
                          onClick={() => {
                            setShowCustomInput(false);
                            setCustomPronunciation('');
                            // Reset tongue state for custom when closing
                            setTongueBetweenTeeth(prev => ({
                              ...prev,
                              custom: false
                            }));
                          }}
                          className="custom-input-close-btn"
                        >
                          <Icon name="close" size="sm" />
                        </button>
                      </div>
                    )}
                    
                    <div className="mispronunciation-option-container">
                      <div className="mispronunciation-option-wrapper">
                        <Form.Check
                          type="checkbox"
                          id="mispronunciation-correct"
                          value="correct"
                          checked={selectedMispronunciations.includes('correct')}
                          onChange={() => toggleMispronunciation('correct')}
                          className="mispronunciation-checkbox d-none"
                        />
                        <label 
                          htmlFor="mispronunciation-correct" 
                          className={`mispronunciation-option-label correct-option ${
                            selectedMispronunciations.includes('correct') ? 'selected' : ''
                          }`}
                        >
                          <Icon name="check" size="sm" className="me-2" />
                          Korrekt ausgesprochen
                        </label>
                      </div>
                      
                      {/* Tongue button for correct pronunciation */}
                      <div className="tongue-button-wrapper">
                        <Form.Check
                          type="checkbox"
                          id="tongue-correct"
                          checked={tongueBetweenTeeth['correct'] || false}
                          onChange={() => toggleTongueForOption('correct')}
                          className="mispronunciation-checkbox d-none"
                        />
                        <label 
                          htmlFor="tongue-correct" 
                          className={`tongue-button ${
                            tongueBetweenTeeth['correct'] ? 'selected' : ''
                          } ${!isTongueButtonEnabled('correct') ? 'disabled' : ''}`}
                          title={isTongueButtonEnabled('correct') ? "Zunge zwischen den Zähnen" : "Wähle zuerst eine Aussprache-Option"}
                        >
                          <Icon name="tongue" size="md" />
                        </label>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* DEBUG: Live problem sounds display (temporary - for testing, only for praxis-logofit) */}
            {auth.currentUser?.email === 'praxis-logofit@outlook.de' && completedTests.length > 0 && (() => {
              const currentSounds = extractProblemSoundsBySeverity(completedTests);
              const hasSounds = currentSounds.severe.length > 0 || currentSounds.mild.length > 0;
              if (!hasSounds) return null;
              return (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#FFF8E1',
                  border: '2px dashed #FFA000',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#E65100' }}>
                    [DEBUG] Erkannte Problemlaute bisher:
                  </div>
                  {currentSounds.severe.length > 0 && (
                    <div style={{ marginBottom: '0.25rem' }}>
                      <span style={{ color: '#DC2626', fontWeight: '600' }}>Schwer: </span>
                      {currentSounds.severe.map(s => (
                        <span key={s} style={{
                          display: 'inline-block',
                          backgroundColor: '#DC2626',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          marginRight: '4px',
                          marginBottom: '2px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>{s}</span>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const filteredMild = currentSounds.mild.filter(m =>
                      !currentSounds.severe.some(s => s.toLowerCase() === m.toLowerCase())
                    );
                    return filteredMild.length > 0 && (
                      <div>
                        <span style={{ color: '#F59E0B', fontWeight: '600' }}>Leicht: </span>
                        {filteredMild.map(s => (
                          <span key={s} style={{
                            display: 'inline-block',
                            backgroundColor: '#FFC107',
                            color: '#000',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginRight: '4px',
                            marginBottom: '2px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>{s}</span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

          </div>
        )}

        {currentStep === 4 && (
          <div className="test-results-content">
            <div className="text-center mb-4">
              <Icon name="check" size="xl" className="mb-3 text-success" />
              <h4 className="mb-2">Test abgeschlossen!</h4>
              <p className="text-muted mb-4">
                Basierend auf den Antworten haben wir folgende Ziellaute identifiziert:
              </p>
            </div>
            
            <div className="problem-sounds-section">
              
              {(severeProblemSounds.length > 0 || mildProblemSounds.length > 0) ? (
                <>
                  {/* Show severe problem sounds first */}
                  {severeProblemSounds.length > 0 && (
                    <div className="mb-4">
                      <h6 className="text-danger mb-3 text-center">Schwere Ziellaute:</h6>
                      <div className="problem-sounds-grid">
                        {severeProblemSounds.map(sound => (
                          <div 
                            key={sound} 
                            className="problem-sound-item"
                            style={{ 
                              backgroundColor: '#DC2626', 
                              borderColor: '#DC2626',
                              color: '#fff' 
                            }}
                          >
                            {sound}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show mild problem sounds below */}
                  {(() => {
                    // Filter out mild sounds that are already in severe sounds (case-insensitive)
                    const filteredMildSounds = mildProblemSounds.filter(mildSound => 
                      !severeProblemSounds.some(severeSound => 
                        severeSound.toLowerCase() === mildSound.toLowerCase()
                      )
                    );
                    
                    return filteredMildSounds.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-warning mb-3 text-center">Leichte Ziellaute:</h6>
                        <div className="problem-sounds-grid">
                          {filteredMildSounds.map(sound => (
                            <div 
                              key={sound} 
                              className="problem-sound-item"
                              style={{ 
                                backgroundColor: '#FFC107', 
                                borderColor: '#FFC107',
                                color: '#000' 
                              }}
                            >
                              {sound}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="text-muted mb-3">
                    <Icon name="check" size="lg" className="mb-2 text-success" />
                    <p>Keine Ziellaute identifiziert</p>
                    <small>Dein Kind spricht alle getesteten Laute korrekt aus!</small>
                  </div>
                </div>
              )}
              
              <div className="text-center mt-4">
                <p className="text-muted mb-3">
                  Diese Laute können jetzt in den Einstellungen für gezieltes Üben ausgewählt werden.
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="aussprache-test-footer">
        <div className="d-flex gap-3 justify-content-between w-100">
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={async () => {
              if (currentStep === 1) {
                // Close the modal from step 1
                await handleClose();
                return;
              } else if (currentStep === 2) {
                // Go back to age selection (step 1)
                setCurrentStep(1);
                await saveTestState({ currentStep: 1 });
              } else if (currentStep === 3 && currentWordIndex > 0) {
                // Go back to previous word
                const prevWordIndex = currentWordIndex - 1;
                const prevTargetWord = availableWords[prevWordIndex];

                // Find the completed test for the previous word
                const completedTest = completedTests.find(test => test.word === prevTargetWord);

                if (completedTest) {
                  // Restore state from the completed test (don't remove it)
                  restoreStateFromCompletedTest(completedTest);
                } else {
                  // No completed test found, start fresh
                  setSelectedMispronunciations([]);
                  setTongueBetweenTeeth({});
                  setCustomPronunciation('');
                  setShowCustomInput(false);
                }

                setCurrentWordIndex(prevWordIndex);

                // Save state with previous word index
                await saveTestState({ currentWordIndex: prevWordIndex });
              } else {
                // Go back to previous step
                const prevStep = currentStep - 1;
                setCurrentStep(prevStep);
                setCurrentWordIndex(0);
                setSelectedMispronunciations([]);
                setTongueBetweenTeeth({});
                setCustomPronunciation('');
                setShowCustomInput(false);

                // Save state with previous step
                await saveTestState({ currentStep: prevStep, currentWordIndex: 0 });
              }
            }}
            className="aussprache-back-button"
          >
            <Icon name="arrow-left" size="sm" className="me-2" />
            <span className="d-none d-md-inline">
              {currentStep === 1 ? 'Abbrechen' :
               currentStep === 2 ? 'Zurück' :
               currentStep === 3 && currentWordIndex > 0 ? 'Vorheriges Wort' :
               'Vorherige Frage'}
            </span>
          </Button>
          
          {currentStep === 1 && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleAgeSubmit}
              disabled={saving}
              className="aussprache-continue-button"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Speichern...
                </>
              ) : (
                <>
                  <Icon name="arrow-right" size="sm" className="me-2" />
                  Weiter
                </>
              )}
            </Button>
          )}

          {currentStep === 2 && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleInfoContinue}
              disabled={saving}
              className="aussprache-continue-button"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Speichern...
                </>
              ) : (
                <>
                  <Icon name="arrow-right" size="sm" className="me-2" />
                  Weiter zum Test
                </>
              )}
            </Button>
          )}

          {currentStep === 3 && (
            <>
              <Button
                variant="outline-warning"
                size="lg"
                onClick={handlePauseTest}
                disabled={saving || pauseConfirmation}
                className="aussprache-pause-button"
                title="Fortschritt speichern und später fortsetzen"
              >
                {pauseConfirmation ? (
                  <span>Gespeichert!</span>
                ) : (
                  <span>Später fortsetzen</span>
                )}
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handlePronunciationSubmit}
                disabled={(selectedMispronunciations.length === 0 && (!showCustomInput || customPronunciation.trim() === '')) || saving || pauseConfirmation}
                className="aussprache-continue-button"
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Speichern...
                  </>
                ) : (
                  <>
                    <Icon name={currentWordIndex < availableWords.length - 1 ? "arrow-right" : "check"} size="sm" className="me-2" />
                    <span className="d-none d-md-inline">
                      {currentWordIndex < availableWords.length - 1 ? 'Nächstes Wort' : 'Test abschließen'}
                    </span>
                  </>
                )}
              </Button>
            </>
          )}

          {currentStep === 4 && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleTestComplete}
              className="aussprache-complete-button"
            >
              <Icon name="check" size="sm" className="me-2" />
              Test abschließen
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AusspracheTestModal;